import { Card, Flex, Skeleton } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useRouteLoaderData } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import { usePluginContext } from "~/contexts/plugin-context";
import type { loader } from "~/root";
import { createTheme } from "~/shared/utils/create-theme";
import { useAuthModals } from "~/widgets/auth-modals";
import type {
  PluginInstance,
  ValueChainLoginPluginProps,
} from "~/widgets/value-chain-login/types";

export const ValueChainLoginPlugin: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { language, authFeatures, siteSdk } = useClientContext();
  const data = useRouteLoaderData<typeof loader>("root");
  const pluginSettings = data?.pluginIntegrations?.valueChainLogin;

  const standaloneUrl = pluginSettings?.standalone_url ?? "";
  const { close, open } = useAuthModals();

  const pageDesignSystem = data?.pageDesignSystem;

  const theme = createTheme(language, {
    colors: pageDesignSystem?.colors ?? {},
    fontSize: pageDesignSystem?.font_size ?? {},
    fontWeight: pageDesignSystem?.font_weight ?? {},
    fontFamily: pageDesignSystem?.font_family ?? {},
    lineHeight: pageDesignSystem?.line_height ?? {},
    borderRadius: pageDesignSystem?.border_radius ?? {},
  });

  const initialParams: ValueChainLoginPluginProps = {
    apiUrl:
      pluginSettings?.api_url ?? "https://casino-registration.jack-casino.com",

    onGoToRegister: async () => {
      open("valueChainRegistration");
    },
    onLogin: async (params) => {
      await siteSdk.setAuthCookie(params);

      const { redirectUrl } = await siteSdk.getRoomUrl(language, "casino/home");
      console.info("go to login");
      window.location.href = redirectUrl;
    },
    onClose: () => {
      console.info("on close");
      close();
    },

    locale: language,
    translations: {
      login: {
        description_link_text:
          pluginSettings?.translations?.[0]?.description_link_text ?? "Sign up",
        description_text:
          pluginSettings?.translations?.[0]?.description_text ??
          "Don’t have an account?",
        form_input_label_email:
          pluginSettings?.translations?.[0]?.form_input_label_email ?? "Email",
        form_input_label_password:
          pluginSettings?.translations?.[0]?.form_input_label_password ??
          "Password",
        form_remember_checkbox_text:
          pluginSettings?.translations?.[0]?.form_remember_checkbox_text ?? "",
        form_submit_text:
          pluginSettings?.translations?.[0]?.form_submit_text ?? "LOG IN",
        form_restore_link_text:
          pluginSettings?.translations?.[0]?.form_restore_link_text ?? "",
        form_title: pluginSettings?.translations?.[0]?.form_title ?? "Log In",
        validation_invalid_credentials:
          authFeatures?.auth?.[0]?.validation_invalid_credentials ?? "",
        validation_required:
          authFeatures?.auth?.[0]?.validation_required ?? "Required field",
        validation_invalid_email:
          authFeatures?.registration?.[0]?.validation_invalid_email ??
          "Invalid email address",
      },
    },
    theme,
  };

  const { loadScript, isLoading } = usePluginContext();

  useEffect(() => {
    if (!standaloneUrl) {
      return;
    }

    let instance: undefined | PluginInstance<ValueChainLoginPluginProps>;

    const init = async (): Promise<void> => {
      await loadScript(standaloneUrl);

      if (!ref.current) return;

      instance = window?.QWSValueChainLogin?.mount(ref.current, initialParams);
    };

    void init();
    return () => {
      instance?.unmount?.();
    };
  }, [ref, initialParams]);

  return isLoading ? (
    <Flex
      height={"100%"}
      flexDirection={"column"}
      justify={"center"}
      align={"center"}
    >
      <Card
        border={{ radius: "card" }}
        variant="default"
        bg="background-tetriary"
        md={{ maxWidth: 525 }}
        minWidth={312}
        maxWidth={440}
        d={{ maxWidth: 525, minWidth: 500 }}
      >
        <Skeleton radius={8} width={"100%"} height={580} />
      </Card>
    </Flex>
  ) : (
    <div ref={ref} id="value-chain-login" />
  );
};
